import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../lib/api';
import type { TakeoverInput } from '../types/pipeline';
import {
  TAKEOVER_RELAYED_KEYS,
  TAKEOVER_RETRY_MS,
  TAKEOVER_TEXT_MAX,
} from '../constants/takeover';

interface Queued {
  input: TakeoverInput;
  seq: number | null;
}

interface Options {
  runId: string;
  onUrl: (url: string) => void;
  /** Resending stopped: a refusal, or the retry budget ran out. The queue is kept until the next send. */
  onRefused: (error: unknown) => void;
}

interface SendState {
  pending: boolean;
  retrying: boolean;
  stalled: boolean;
}

export const statusOf = (error: unknown): number | null =>
  (error as { response?: { status?: number } })?.response?.status ?? null;

/** True when the held page is gone for good: the hold lapsed (409) or the run is not ours. */
export const takeoverIsOver = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === 409 || status === 403 || status === 404;
};

/** What the person's unsent input is doing, or null when everything has landed. */
export const sendingStatus = ({ pending, retrying, stalled }: SendState): string | null => {
  if (stalled) return 'Some of what you typed has not reached the page. Type again to retry.';
  if (retrying) return 'The connection is slow. Still sending what you typed.';
  return pending ? 'Sending what you typed…' : null;
};

/** True for the combination that moves focus off the stage back to the controls. */
export const isLeaveStageKey = (e: React.KeyboardEvent | KeyboardEvent): boolean =>
  e.key === 'Escape' && e.shiftKey;

/** The input a key press stands for on the held page, or null when it stays here. */
export const keyInput = (e: React.KeyboardEvent | KeyboardEvent): TakeoverInput | null => {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.key.length === 1) return { action: 'type', text: e.key };
  if (TAKEOVER_RELAYED_KEYS.has(e.key)) return { action: 'press', key: e.key };
  return null;
};

const worthResending = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === null || status === 408 || status === 429 || status >= 500;
};

/** The one input an unsent queued input and a new one stand for together, or null. */
const joined = (last: Queued | undefined, next: TakeoverInput): TakeoverInput | null => {
  if (!last || last.seq !== null) return null;
  const prev = last.input;
  if (prev.action === 'type' && next.action === 'type') {
    const text = prev.text + next.text;
    return text.length <= TAKEOVER_TEXT_MAX ? { action: 'type', text } : null;
  }
  if (prev.action === 'scroll' && next.action === 'scroll') {
    return { action: 'scroll', dy: prev.dy + next.dy };
  }
  return null;
};

const newStream = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Relays a person's input to a held page: queued in order, consecutive typing and scrolling
 * coalesced, each item resent until the server acknowledges its sequence number.
 */
export const useTakeoverInput = ({ runId, onUrl, onRefused }: Options) => {
  const queueRef = useRef<Queued[]>([]);
  const sendingRef = useRef(false);
  const stoppedRef = useRef(false);
  const genRef = useRef(0);
  const seqRef = useRef(0);
  const streamRef = useRef(newStream());
  const handlersRef = useRef({ onUrl, onRefused });
  const [queued, setQueued] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    handlersRef.current = { onUrl, onRefused };
  }, [onUrl, onRefused]);

  const pump = useCallback(async () => {
    const send = async (head: Queued) => {
      head.seq ??= ++seqRef.current;
      const res = await apiClient.sendTakeoverInput(runId, head.input, {
        stream: streamRef.current,
        seq: head.seq,
      });
      if (res.seq != null && res.seq !== head.seq) throw new Error('takeover ack is for another input');
      return res;
    };
    if (sendingRef.current) return;
    sendingRef.current = true;
    const gen = genRef.current;
    const live = () => !stoppedRef.current && genRef.current === gen;
    let delay = TAKEOVER_RETRY_MS.first;
    let tries = 0;
    setStalled(false);
    while (queueRef.current.length && live()) {
      const head = queueRef.current[0];
      try {
        const res = await send(head);
        if (!live()) break;
        if (queueRef.current[0] === head) queueRef.current.shift();
        setQueued(queueRef.current.length);
        setRetrying(false);
        delay = TAKEOVER_RETRY_MS.first;
        tries = 0;
        if (res.url) handlersRef.current.onUrl(res.url);
      } catch (error) {
        if (!live()) break;
        if (!worthResending(error) || ++tries >= TAKEOVER_RETRY_MS.tries) {
          setRetrying(false);
          setStalled(true);
          handlersRef.current.onRefused(error);
          break;
        }
        console.error('takeover input not acknowledged, resending:', error);
        setRetrying(true);
        await wait(delay);
        delay = Math.min(delay * 2, TAKEOVER_RETRY_MS.max);
      }
    }
    if (genRef.current === gen) sendingRef.current = false;
  }, [runId]);

  const enqueue = useCallback(
    (input: TakeoverInput) => {
      if (stoppedRef.current) return;
      const queue = queueRef.current;
      const last = queue[queue.length - 1];
      const merged = joined(last, input);
      if (merged) last.input = merged;
      else queue.push({ input, seq: null });
      setQueued(queue.length);
      void pump();
    },
    [pump],
  );

  const typeText = useCallback(
    (text: string) => {
      for (let i = 0; i < text.length; i += TAKEOVER_TEXT_MAX) {
        enqueue({ action: 'type', text: text.slice(i, i + TAKEOVER_TEXT_MAX) });
      }
    },
    [enqueue],
  );

  const clear = useCallback(() => {
    queueRef.current = [];
    setQueued(0);
    setRetrying(false);
    setStalled(false);
  }, []);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    clear();
  }, [clear]);

  const resume = useCallback(() => {
    stoppedRef.current = false;
  }, []);

  const pending = queued > 0 && !stalled;

  useEffect(() => {
    if (!pending) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pending]);

  useEffect(() => {
    stoppedRef.current = false;
    genRef.current += 1;
    sendingRef.current = false;
    streamRef.current = newStream();
    seqRef.current = 0;
    return () => {
      stoppedRef.current = true;
      genRef.current += 1;
      queueRef.current = [];
    };
  }, [runId]);

  return { send: enqueue, typeText, stop, resume, pending, retrying, stalled };
};
